import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Youtube, Music2, Twitter } from "lucide-react";
import { LogoWordmark, SignalLines } from "@/components/Logo";

export function SiteFooter({ config }) {
  const settings = config?.settings || {};
  const links = settings.social_links || {};
  const year = new Date().getFullYear();
  const socials = [
    { key: "instagram", icon: Instagram, label: "Instagram" },
    { key: "youtube", icon: Youtube, label: "YouTube" },
    { key: "tiktok", icon: Music2, label: "TikTok" },
    { key: "spotify", icon: Music2, label: "Spotify" },
    { key: "x", icon: Twitter, label: "X" },
  ].filter((s) => links[s.key]);

  return (
    <footer className="mt-auto border-t border-border bg-card" data-testid="site-footer">
      <div className="ss-container grid gap-10 py-12 md:grid-cols-[1.4fr_1fr_1fr]">
        <div>
          <LogoWordmark />
          <SignalLines className="mt-4 block" />
          {settings.organizer_logo_url && (
            <a
              href={settings.organizer_website || "https://sembangohhyeahh.com"}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 flex w-fit items-center gap-3 rounded-xl border border-[hsl(var(--ss-line))] bg-[hsl(var(--ss-panel))] p-2.5"
              data-testid="footer-organizer-logo"
            >
              <img
                src={settings.organizer_logo_url}
                alt={`Logo ${settings.organizer_name || "penganjur"}`}
                className="h-11 w-11 rounded-lg object-cover"
                width="44"
                height="44"
                loading="lazy"
              />
              <span className="pr-1 text-[11px] font-medium uppercase tracking-[0.16em] text-white/70">
                Inisiatif oleh
                <span className="block text-[13px] font-semibold normal-case tracking-normal text-white">
                  {settings.organizer_name}
                </span>
              </span>
            </a>
          )}
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-[hsl(var(--muted-foreground))]">
            Kempen penyertaan nasional yang dibuat secara bebas oleh{" "}
            <span className="font-semibold text-foreground">
              {settings.organizer_name || "SembangOhhyeahh"}
            </span>
            . Satu soalan. Satu peluang untuk didengar.
          </p>
          {socials.length > 0 && (
            <div className="mt-5 flex gap-2">
              {socials.map((s) => (
                <a
                  key={s.key}
                  href={links[s.key]}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  aria-label={s.label}
                  className="rounded-xl border border-border p-2 text-foreground transition-colors hover:border-[hsl(var(--ss-accent))] hover:text-[hsl(var(--ss-accent))]"
                  data-testid={`footer-social-${s.key}`}
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          )}
        </div>

        <nav aria-label="Pautan kempen">
          <h2 className="text-xs font-bold uppercase tracking-[0.16em] text-foreground">
            Kempen
          </h2>
          <ul className="mt-4 space-y-2.5 text-sm text-[hsl(var(--muted-foreground))]">
            <li><Link to="/hantar-soalan" className="hover:text-[hsl(var(--ss-accent))]" data-testid="footer-link-hantar">Hantar Soalan</Link></li>
            <li><Link to="/cara-pemilihan" className="hover:text-[hsl(var(--ss-accent))]" data-testid="footer-link-cara">Cara Pemilihan</Link></li>
            <li><Link to="/keputusan" className="hover:text-[hsl(var(--ss-accent))]" data-testid="footer-link-keputusan">Keputusan</Link></li>
            <li><Link to="/tonton" className="hover:text-[hsl(var(--ss-accent))]" data-testid="footer-link-tonton">Tonton</Link></li>
            {settings.public_gallery_enabled && (
              <li><Link to="/soalan-rakyat" className="hover:text-[hsl(var(--ss-accent))]" data-testid="footer-link-galeri">Soalan Rakyat</Link></li>
            )}
          </ul>
        </nav>

        <nav aria-label="Pautan ketelusan">
          <h2 className="text-xs font-bold uppercase tracking-[0.16em] text-foreground">
            Ketelusan
          </h2>
          <ul className="mt-4 space-y-2.5 text-sm text-[hsl(var(--muted-foreground))]">
            <li><Link to="/faq" className="hover:text-[hsl(var(--ss-accent))]" data-testid="footer-link-faq">FAQ</Link></li>
            <li><Link to="/privasi" className="hover:text-[hsl(var(--ss-accent))]" data-testid="footer-link-privasi">Notis Privasi</Link></li>
            <li><Link to="/terma" className="hover:text-[hsl(var(--ss-accent))]" data-testid="footer-link-terma">Terma Penyertaan</Link></li>
            <li><span className="font-semibold text-foreground">#SatuSoalanPMX</span></li>
          </ul>
        </nav>
      </div>

      <div className="border-t border-border bg-[hsl(var(--secondary))]">
        <div className="ss-container flex flex-col gap-3 py-6">
          <p
            className="max-w-3xl text-[13px] leading-relaxed text-[hsl(var(--muted-foreground))]"
            data-testid="footer-disclaimer"
          >
            {settings.disclaimer_text ||
              "Inisiatif bebas oleh SembangOhhyeahh. Bukan laman rasmi Pejabat Perdana Menteri atau Kerajaan Malaysia. Penggunaan nama PMX merujuk kepada tetamu sesi podcast."}
          </p>
          <p className="text-[12px] font-medium text-foreground" data-testid="footer-powered-by">
            Powered By Underads Media
          </p>
          <p className="text-[12px] text-[hsl(var(--muted-foreground))]">
            © {year} {settings.organizer_name || "SembangOhhyeahh"} · satusoalan.com · Keputusan kempen
            menggambarkan peserta kempen sahaja, bukan seluruh rakyat Malaysia.
          </p>
        </div>
      </div>
    </footer>
  );
}
